import { useState, useEffect, useCallback, useRef } from "react";
import DailyIframe from "@daily-co/daily-js";
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  Phone, 
  Monitor,
  Loader2,
  AlertCircle,
  ExternalLink,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

export function VideoCall({ bookingId, onLeave }) {
  const [callFrame, setCallFrame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [participantCount, setParticipantCount] = useState(1);
  const [meetingInfo, setMeetingInfo] = useState(null);
  const containerRef = useRef(null);

  const initializeCall = useCallback(async () => {
    if (!containerRef.current) {
      console.error("Container ref not available");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("Fetching meeting token for booking:", bookingId);
      
      // Get meeting token from backend
      const response = await axios.get(`${API}/bookings/${bookingId}/meeting-token`);
      const { token, room_url, user_name, room_name } = response.data;
      
      console.log("Meeting token received:", { room_url, room_name, user_name });
      setMeetingInfo({ room_url, token, user_name });

      // Destroy existing frame if any
      if (callFrame) {
        try {
          await callFrame.destroy();
        } catch (e) {
          console.warn("Error destroying old frame:", e);
        }
      }

      // Create Daily iframe with prebuilt UI - CONTAINED within app
      const frame = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "none",
          borderRadius: "12px",
          backgroundColor: "#1a1a2e",
        },
        // Disable fullscreen to keep within app bounds
        showLeaveButton: true,
        showFullscreenButton: false, // Disabled to keep in-app
        showParticipantsBar: true,
        showLocalVideo: true,
        showChat: false, // Keep UI minimal on mobile
        theme: {
          colors: {
            accent: "#d4af37",
            accentText: "#000000",
            background: "#1a1a2e",
            backgroundAccent: "#2a2a4e",
            baseText: "#ffffff",
            border: "#3a3a5e",
            mainAreaBg: "#0a0a1e",
            mainAreaBgAccent: "#1a1a2e",
            mainAreaText: "#ffffff",
            supportiveText: "#888888",
          },
        },
      });

      // Set up event listeners
      frame.on("joining-meeting", () => {
        console.log("Joining meeting...");
      });

      frame.on("joined-meeting", () => {
        console.log("Successfully joined meeting");
        setLoading(false);
        toast.success("Connected to video call");
      });

      frame.on("participant-joined", (event) => {
        console.log("Participant joined:", event);
        setParticipantCount((prev) => prev + 1);
        if (event.participant?.user_name && !event.participant.local) {
          toast.info(`${event.participant.user_name} joined`);
        }
      });

      frame.on("participant-left", (event) => {
        console.log("Participant left:", event);
        setParticipantCount((prev) => Math.max(1, prev - 1));
        if (event.participant?.user_name && !event.participant.local) {
          toast.info(`${event.participant.user_name} left`);
        }
      });

      frame.on("left-meeting", () => {
        console.log("Left meeting");
        if (onLeave) onLeave();
      });

      frame.on("error", (event) => {
        console.error("Daily error event:", event);
        const errorMsg = event?.error?.message || event?.errorMsg || "Connection error";
        setError(errorMsg);
        setLoading(false);
        toast.error(`Video call error: ${errorMsg}`);
      });

      frame.on("camera-error", (event) => {
        console.error("Camera error:", event);
        toast.error("Camera access denied or unavailable. Please check your browser permissions.");
      });

      frame.on("local-screen-share-started", () => {
        setIsScreenSharing(true);
      });

      frame.on("local-screen-share-stopped", () => {
        setIsScreenSharing(false);
      });

      // Join the meeting
      console.log("Attempting to join meeting at:", room_url);
      await frame.join({ url: room_url, token });
      setCallFrame(frame);

    } catch (err) {
      console.error("Failed to initialize call:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to connect to video call";
      setError(errorMessage);
      setLoading(false);
      toast.error(errorMessage);
    }
  }, [bookingId, onLeave]);

  useEffect(() => {
    initializeCall();

    return () => {
      if (callFrame) {
        callFrame.destroy().catch(console.error);
      }
    };
  }, []);

  const toggleCamera = () => {
    if (callFrame) {
      callFrame.setLocalVideo(!isCameraOn);
      setIsCameraOn(!isCameraOn);
    }
  };

  const toggleMic = () => {
    if (callFrame) {
      callFrame.setLocalAudio(!isMicOn);
      setIsMicOn(!isMicOn);
    }
  };

  const toggleScreenShare = async () => {
    if (callFrame) {
      if (isScreenSharing) {
        callFrame.stopScreenShare();
      } else {
        callFrame.startScreenShare();
      }
    }
  };

  const leaveCall = () => {
    if (callFrame) {
      callFrame.leave();
    }
    if (onLeave) onLeave();
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] bg-slate-900 rounded-xl p-6 sm:p-8">
        <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 text-center">Connection Error</h3>
        <p className="text-sm sm:text-base text-slate-400 text-center mb-4 max-w-md">{error}</p>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Button onClick={initializeCall} className="bg-gold-500 hover:bg-gold-400 text-black">
            <Video className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          {meetingInfo?.room_url && (
            <Button 
              onClick={() => window.open(meetingInfo.room_url, '_blank')}
              variant="outline"
              className="border-slate-600"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Browser
            </Button>
          )}
        </div>
        <p className="text-slate-500 text-xs mt-4 text-center">
          If the issue persists, try using Chrome or Safari browser
        </p>
      </div>
    );
  }

  return (
    <div className="relative bg-slate-900 rounded-xl overflow-hidden h-full flex flex-col">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
          <div className="text-center px-4">
            <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-gold-500 animate-spin mx-auto mb-4" />
            <p className="text-white text-base sm:text-lg">Connecting...</p>
            <p className="text-slate-400 text-xs sm:text-sm">Please allow camera & mic access</p>
          </div>
        </div>
      )}
      
      {/* Video Container - Fills available space */}
      <div 
        ref={containerRef} 
        className="flex-1 min-h-0 w-full"
        style={{ minHeight: "250px" }}
      />
      
      {/* Custom Controls Bar - Fixed at bottom of video area */}
      {!loading && (
        <div className="shrink-0 bg-slate-900/95 border-t border-slate-800 px-3 py-3 sm:px-4 sm:py-4">
          {/* Participant count */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-xs sm:text-sm text-slate-400">
              {participantCount} {participantCount === 1 ? "participant" : "participants"}
            </span>
          </div>
          
          {/* Control buttons */}
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <Button
              onClick={toggleCamera}
              variant="outline"
              size="icon"
              className={`rounded-full w-11 h-11 sm:w-12 sm:h-12 ${
                isCameraOn 
                  ? "bg-slate-700 hover:bg-slate-600 border-slate-600" 
                  : "bg-red-600 hover:bg-red-500 border-red-600"
              }`}
              data-testid="toggle-camera"
            >
              {isCameraOn ? (
                <Video className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              ) : (
                <VideoOff className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              )}
            </Button>
            
            <Button
              onClick={toggleMic}
              variant="outline"
              size="icon"
              className={`rounded-full w-11 h-11 sm:w-12 sm:h-12 ${
                isMicOn 
                  ? "bg-slate-700 hover:bg-slate-600 border-slate-600" 
                  : "bg-red-600 hover:bg-red-500 border-red-600"
              }`}
              data-testid="toggle-mic"
            >
              {isMicOn ? (
                <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              ) : (
                <MicOff className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              )}
            </Button>
            
            {/* Screen share - only show on larger screens */}
            <Button
              onClick={toggleScreenShare}
              variant="outline"
              size="icon"
              className={`rounded-full w-11 h-11 sm:w-12 sm:h-12 hidden sm:flex ${
                isScreenSharing 
                  ? "bg-green-600 hover:bg-green-500 border-green-600" 
                  : "bg-slate-700 hover:bg-slate-600 border-slate-600"
              }`}
              data-testid="toggle-screenshare"
            >
              <Monitor className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </Button>
            
            {/* Leave Call Button - More prominent */}
            <Button
              onClick={leaveCall}
              className="rounded-full h-11 sm:h-12 px-5 sm:px-6 bg-red-600 hover:bg-red-500 text-white font-medium"
              data-testid="leave-call"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 rotate-[135deg] mr-2" />
              <span className="text-sm sm:text-base">Leave</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoCall;
